import { ChipGroup } from "@newsela/angelou";
import { ChangeEvent, useEffect } from "react";
import {
  actionNames,
  actionPrefixes,
  eventTypes,
  objectTypes,
  useTracker,
} from "@newsela/snitch";

import { ContentHeaderSortByButton } from "@/search/Langston/ContentTypeFilter/ContentHeaderSortByButton";
import { useSearchPageContext } from "@/search/Langston/SearchPageProvider";
import { useFilterControls } from "@/search/Langston/useFilterControls";
import { useSearchAdminPermissions } from "@/search/Langston/useSearchAdminPermissions";
import { LangstonParameterSchema } from "@/search/Langston/LangstonParameterSchema";
import { useContentTypeValues } from "@/search/Langston/LangstonParameterSchema/useContentTypeValues";
import {
  chipGroupRootCss,
  contentTypesVisibilityCss,
} from "./ContentTypeFilter.styles";
import { ContentTypeFilterChips } from "./ContentTypeFilterChips";

const WORKSHEET_ENUM = "WORKSHEET";

export const ContentTypeFilter = () => {
  const {
    getters: { isDisabledFilters, selectedWorksheet },
    handlers: {
      getSearchParamValues,
      handleWorksheets,
      toggleSearchParamValue,
      deleteSearchParamValues,
    },
  } = useSearchPageContext();

  // Using a custom tracking instead of withTrackerEvent to avoid accessibility issues and also the withTrackerEvent with Chip component doesn't work properly
  const { trackAction, getEventName } = useTracker();

  const { blockActionAndShowConfirmationModal, blockFilterChanges } =
    useSearchAdminPermissions();

  const { handleClearAllFilters } = useFilterControls();

  const contentTypeValues = useContentTypeValues();

  const isWorthsheetsEnabled = isDisabledFilters;

  const handleTrackAction = (value: string) =>
    trackAction({
      event_name: getEventName(
        objectTypes.content,
        actionPrefixes.initiate,
        actionNames.filter,
      ),
      event_type: eventTypes.click,
      dimension_properties: {
        description: `Filter search by ${value} Content Type`,
      },
    });

  const handleChipSelection = (
    event: ChangeEvent<HTMLInputElement>,
    value: string,
  ) => {
    if (blockFilterChanges) {
      blockActionAndShowConfirmationModal();
      return;
    }

    if (value === WORKSHEET_ENUM) {
      if (!isDisabledFilters) handleClearAllFilters();
      handleWorksheets(event.target.checked);
      handleTrackAction(value);
      return;
    }

    if (isDisabledFilters && value !== "all") {
      return;
    }

    handleTrackAction(value);

    if (value === "all") {
      if (isDisabledFilters) handleWorksheets(false);
      deleteSearchParamValues(LangstonParameterSchema.content_types.key);
      return;
    }

    const values = value.split("&");
    values.forEach((v) => {
      toggleSearchParamValue(LangstonParameterSchema.content_types.key, v);
    });
  };

  useEffect(() => {
    if (selectedWorksheet) {
      handleWorksheets(true);
    }
  }, [selectedWorksheet]);

  if (!contentTypeValues) return null;

  return (
    <div css={contentTypesVisibilityCss}>
      <ChipGroup
        __cssFor={chipGroupRootCss}
        chipType={ChipGroup.chipTypes.filter}
        groupName="selectedFilters"
        onChipEvent={handleChipSelection}
        size={ChipGroup.chipSizes.medium}
      >
        <ContentTypeFilterChips
          activeChips={getSearchParamValues(
            LangstonParameterSchema.content_types.key,
          )}
          contentTypeValues={contentTypeValues}
          isWorthsheetsEnabled={isWorthsheetsEnabled}
        />
      </ChipGroup>

      <ContentHeaderSortByButton />
    </div>
  );
};
